import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { ChartScatter } from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One column of the visual-analysis trifecta (Level / Trend / Variability). */
export interface VisualAnalysisStat {
  /** Uppercase caption, e.g. "Level", "Trend", "Variability". */
  label: string;
  /** Primary value, e.g. "14.8", "-0.91", "42.5%". */
  value: string;
  /** Unit / descriptor under the value, e.g. "mean", "slope/session", "CV". */
  legend: string;
  /** Qualitative detail, e.g. "15.0 median", "Decelerating", "High". */
  detail: string;
}

export interface VisualAnalysisSummaryCardProps {
  /** Card heading. Defaults to "Visual Analysis Summary". */
  title?: string;
  /** Icon element rendered in the badge. Defaults to a scatter-chart icon. */
  icon?: React.ReactNode;
  /** The visual-analysis stats — typically Level, Trend, and Variability. */
  stats: VisualAnalysisStat[];
  /** Additional MUI sx overrides. */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// VisualAnalysisSummaryCard
// ---------------------------------------------------------------------------

/**
 * Presentational "Visual Analysis Summary" card for free-operant targets —
 * the level / trend / variability trifecta. Each stat shows a value, a unit
 * legend, and a qualitative detail. All values are pre-computed by the caller.
 *
 * @param props
 * @param [props.title="Visual Analysis Summary"] - Card heading
 * @param [props.icon] - Badge icon (defaults to a scatter-chart icon)
 * @param props.stats - Level / Trend / Variability stats
 * @param [props.sx] - Additional MUI sx overrides
 */
const VisualAnalysisSummaryCard: React.FC<VisualAnalysisSummaryCardProps> = ({
  title = "Visual Analysis Summary",
  icon = <ChartScatter size={24} />,
  stats,
  sx,
}) => {
  return (
    <Box
      component="section"
      aria-label={title}
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 7,
          width: "100%",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* Header */}
      <Stack alignItems="center" gap={3}>
        <Box
          aria-hidden
          sx={{
            display: "inline-flex",
            p: 3,
            borderRadius: "50%",
            bgcolor: "grayscale.100",
            color: "text.secondary",
          }}
        >
          {icon}
        </Box>
        <Typography
          component="p"
          variant="h2"
          sx={{ fontWeight: 500, color: "text.primary", textAlign: "center" }}
        >
          {title}
        </Typography>
      </Stack>

      {/* Stats trifecta */}
      <Stack component="dl" direction="row" gap={6} sx={{ m: 0, justifyContent: "center" }}>
        {stats.map((stat) => (
          <Stack key={stat.label} alignItems="center" gap={0}>
            <Typography
              component="dt"
              variant="overline"
              sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "1px" }}
            >
              {stat.label}
            </Typography>
            <Typography component="dd" variant="h4" sx={{ m: 0, color: "text.primary" }}>
              {stat.value}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {stat.legend}
            </Typography>
            <Typography variant="body1" sx={{ color: "text.primary", textAlign: "center" }}>
              {stat.detail}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default VisualAnalysisSummaryCard;
